/**
 * SCOPE dropdown (BASE BID / ALTERNATE n / OPTION n…) used by the item and assembly editors, the zone and
 * fixture cards. "Inherit" = blank (the zone / type / frame / BASE BID decides). With a linked workbook the
 * list is DASHBOARDS' scope register first; anything else is usable but flagged, since the workbook's SCOPE
 * INTEGRITY check expects every SCOPE to be registered. "Other…" takes a typed name.
 */
import { useState } from 'react';
import type { ScopeChoices } from '../scopes';

const OTHER = '__other__';

export function ScopeSelect(p: {
  value: string;
  onChange(scope: string): void;
  choices: ScopeChoices;
  /** What "inherit" resolves to here, e.g. "frame / BASE BID". */
  inheritLabel?: string;
  disabled?: boolean;
  testId?: string;
  size?: 'sm';
}) {
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState('');
  const known = [...p.choices.registered, ...p.choices.other];
  const value = p.value.trim();
  const unregistered = !!value && p.choices.fromWorkbook && !p.choices.registered.includes(value);
  if (typing) {
    const done = () => {
      setTyping(false);
      if (draft.trim()) p.onChange(draft.trim().toUpperCase());
    };
    return (
      <span className="rl-scope-other">
        <input
          className="rl-input"
          data-size={p.size}
          autoFocus
          value={draft}
          placeholder="OPTION 1 - LED SCREEN"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') done();
            if (e.key === 'Escape') setTyping(false);
          }}
          onBlur={done}
          aria-label="New SCOPE name"
          data-testid={p.testId ? `${p.testId}-other` : undefined}
        />
      </span>
    );
  }
  return (
    <span className="rl-scope">
      <select
        className="rl-select"
        data-size={p.size}
        value={value}
        disabled={p.disabled}
        onChange={(e) => {
          if (e.target.value === OTHER) {
            setDraft('');
            setTyping(true);
            return;
          }
          p.onChange(e.target.value);
        }}
        aria-label="SCOPE"
        data-testid={p.testId}
      >
        <option value="">Inherit{p.inheritLabel ? ` (${p.inheritLabel})` : ''}</option>
        {p.choices.fromWorkbook ? (
          <>
            <optgroup label="In the workbook's scope register">
              {p.choices.registered.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </optgroup>
            {p.choices.other.length ? (
              <optgroup label="Not registered on DASHBOARDS yet">
                {p.choices.other.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </>
        ) : (
          known.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))
        )}
        {value && !known.includes(value) ? <option value={value}>{value}</option> : null}
        <option value={OTHER}>Other…</option>
      </select>
      {unregistered ? (
        <span className="rl-scope-warn" title="Add it to the scope register on DASHBOARDS so the workbook prices it (its SCOPE INTEGRITY check flags unregistered scopes)" data-testid={p.testId ? `${p.testId}-unregistered` : undefined}>
          not in the workbook's scope register — add it on DASHBOARDS
        </span>
      ) : null}
    </span>
  );
}
